import { useState, useEffect } from "react";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Star,
  Save, 
  X,
  User,
  MapPin,
  Calendar,
  CheckCircle,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Bathroom Renovation",
  "Kitchen Tiling",
  "Floor Tiling",
  "Shower Room",
  "En-suite",
  "Wet Room",
  "Commercial",
  "General"
];

const ReviewManager = ({ token }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    author: "",
    rating: 5,
    text: "",
    project_type: "General",
    location: "",
    date: "",
    verified: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/owner/reviews`, { headers });
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      author: "",
      rating: 5,
      text: "",
      project_type: "General",
      location: "",
      date: new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      verified: false
    });
    setIsCreating(true);
    setEditingReview(null);
  };

  const handleEdit = (review) => {
    setFormData({
      author: review.author || "",
      rating: review.rating || 5,
      text: review.text || "",
      project_type: review.project_type || "General",
      location: review.location || "",
      date: review.date || "",
      verified: review.verified || false
    });
    setEditingReview(review);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setEditingReview(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!formData.author.trim() || !formData.text.trim()) {
      toast.error("Author name and review text are required");
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        await axios.post(`${API}/owner/reviews`, formData, { headers });
        toast.success("Review created");
      } else if (editingReview) {
        await axios.put(`${API}/owner/reviews/${editingReview.id}`, formData, { headers });
        toast.success("Review updated");
      }
      handleCancel();
      fetchReviews();
    } catch (error) {
      toast.error("Failed to save review");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    
    try {
      await axios.delete(`${API}/owner/reviews/${reviewId}`, { headers });
      toast.success("Review deleted");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const renderStars = (rating, editable = false) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => editable && setFormData({ ...formData, rating: star })}
            className={editable ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
            disabled={!editable}
          >
            <Star 
              className={`w-5 h-5 ${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} 
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Edit/Create Form
  if (isCreating || editingReview) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="review-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {isCreating ? "Add New Review" : "Edit Review"}
          </h2>
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="e.g. John Smith"
                className="mt-1"
                data-testid="review-author-input"
              />
            </div>
            <div>
              <Label>Rating *</Label>
              <div className="mt-2">
                {renderStars(formData.rating, true)}
              </div>
            </div>
          </div>

          <div>
            <Label>Review Text *</Label>
            <textarea
              value={formData.text}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="Write the customer's review..."
              className="mt-1 w-full min-h-[120px] px-3 py-2 rounded-md border border-input bg-background text-sm"
              data-testid="review-text-input"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Project Type</Label>
              <select
                value={formData.project_type}
                onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="review-project-type-select"
              >
                {PROJECT_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. Didsbury"
                className="mt-1"
                data-testid="review-location-input"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                placeholder="e.g. March 2026"
                className="mt-1"
                data-testid="review-date-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.verified}
              onCheckedChange={(checked) => setFormData({ ...formData, verified: checked })}
              data-testid="review-verified-checkbox"
            />
            <Label className="cursor-pointer">Verified Review (from Google or confirmed customer)</Label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={saving} data-testid="save-review-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Review"}
            </Button>
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  // Review List
  return (
    <div data-testid="review-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Customer Reviews</h2>
          <p className="text-sm text-gray-500">{reviews.length} reviews</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-review-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Review
        </Button>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No reviews yet</p>
          <Button className="mt-4" onClick={handleCreate}>Add Your First Review</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div 
              key={review.id} 
              className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-shadow"
              data-testid={`review-card-${review.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{review.author}</h3>
                        {review.verified && (
                          <CheckCircle className="w-4 h-4 text-green-500" title="Verified Review" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {renderStars(review.rating)}
                        {review.date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {review.date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-3 pl-13">{review.text}</p>
                  
                  <div className="flex items-center gap-3 text-xs text-gray-500 pl-13">
                    {review.project_type && (
                      <span className="px-2 py-0.5 bg-primary/10 text-primary rounded">
                        {review.project_type}
                      </span>
                    )}
                    {review.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {review.location}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-1 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(review)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(review.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewManager;
